import PropTypes from 'prop-types';
import { format } from 'date-fns';
import _ from 'lodash';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    Divider,
    Avatar,
    Stack,
} from '@mui/material';
import logo from 'assets/images/vivekanand.png';
import { useHospitalDetails } from 'api/cutsom-hooks';
import { numberToWords, formatPrice } from 'pages/invoice/utils';
import { normalizeHospitalDetails, pickHospitalLogo, pickHospitalPhone } from 'utils/gst';

export default function SaleInvoiceA4Content({ formData }) {
    const configuredHospital = useHospitalDetails();
    const hospitalSource = {
        ...(formData?.hospital_details || {}),
        hospital_gst_number: formData?.hospital_gst_number,
        pan_number: formData?.hospital_pan_number,
        ...(configuredHospital || {})
    };
    const hospitalDetails = normalizeHospitalDetails(hospitalSource);
    const hospitalLogo = pickHospitalLogo(hospitalSource, logo);
    const hospitalPhone = pickHospitalPhone(hospitalSource);

    const {
        invoice_number,
        total_amount,
        created_at,
        payment_method,
        wholesaler_supplier_name,
        store_name,
        store
    } = formData;
    
    const baseItems = formData?.items?.pharmacy || [];

    const normalizedItems = baseItems.flatMap((item) => {
        if (Array.isArray(item.batches) && item.batches.length > 0) {
            return item.batches.map((batch) => ({
                ...item,
                quantity: batch.quantity ?? item.quantity,
                batch_number: batch.batch_number ?? item.batch_number,
                expiry_date: batch.expiry_date ?? item.expiry_date,
                selling_price: batch.selling_price ?? batch.sellingPrice ?? item.selling_price,
                buying_price: batch.buying_price ?? batch.unitCost ?? item.buying_price ?? item.unit_price,
                discount_in_perc: batch.discount ?? item.discount_in_perc ?? 0,
            }));
        }
        return [item];
    });

    const items = normalizedItems;

    const getMRP = (item) => {
        return item.selling_price ? parseFloat(item.selling_price) : null;
    };

    const getPrice = (item) => {
        return parseFloat(item.buying_price || item.selling_price || 0);
    };

    const invoiceDate = new Date(created_at);
    const formattedDate = format(invoiceDate, 'dd-MM-yyyy');
    const formattedTime = format(invoiceDate, 'HH:mm');
    const subtotal = items?.reduce((sum, item) => sum + Number(getPrice(item) * item.quantity || 0), 0);
    const totalDiscount = items?.reduce((sum, item) =>
        sum + (Number(getPrice(item) * item.quantity || 0) * Number(item.discount_in_perc || 0)) / 100, 0);
    // Calculate GST summaries if backend returned them
    const hasGst = items.some(item => parseFloat(item.gst_rate || item.tax_in_perc || item.gst_percentage || 0) > 0);
    const isIgst = items.some(item => parseFloat(item.igst || 0) > 0);
    
    let totalTaxableValue = 0;
    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;
    let totalTax = 0;

    items.forEach(item => {
        totalTaxableValue += parseFloat(item.taxable_value || 0);
        totalCgst += parseFloat(item.cgst || 0);
        totalSgst += parseFloat(item.sgst || 0);
        totalIgst += parseFloat(item.igst || 0);
    });

    if (hasGst) {
        totalTax = totalCgst + totalSgst + totalIgst;
    } else {
        totalTax = items?.reduce((sum, item) =>
            sum + (Number(getPrice(item) * item.quantity || 0) * (1 - Number(item.discount_in_perc || 0) / 100) * Number(item.tax_in_perc || 0)) / 100, 0);
    }
    
    const grandTotal = parseFloat(total_amount);
    const totalInWords = `Rs. ${numberToWords(Math.round(grandTotal))} Only`;
    const roundOff = Math.abs(Number(formData?.round_off || 0));
    const isPositiveRoundOff = Number(formData?.round_off || 0) >= 0;

    return (
        <Box>
            <Box sx={{ p: 3, '@media print': { p: 1 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ textAlign: 'right' }}>
                        <Avatar
                            src={hospitalLogo}
                            alt="Logo"
                            sx={{ width: 50, height: 50, border: '2px solid black', backgroundColor: 'white', mx: 'auto' }}
                        />
                        {hospitalDetails.hospital_code && (
                            <Typography variant="subtitle1" fontWeight="bold" align="center">
                                {hospitalDetails.hospital_code}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h5" marginY={1} gutterBottom>
                            {hospitalDetails.name}
                        </Typography>
                        {hospitalDetails.address && (
                            <Typography variant="body2">{hospitalDetails.address}</Typography>
                        )}
                        {hospitalPhone && (
                            <Typography variant="body2">Phone: {hospitalPhone}</Typography>
                        )}
                        {hospitalDetails.gstNumber && (
                            <Typography variant="body2">GST: {hospitalDetails.gstNumber}</Typography>
                        )}
                    </Box>
                </Box>

                {/* Patient & Invoice Info */}
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <Typography><strong>Bill No.:</strong> {invoice_number}</Typography>
                        <Typography><strong>Supplier:</strong> {wholesaler_supplier_name}</Typography>
                        <Typography><strong>Store:</strong> {store?.name || store_name}</Typography>
                        {formData.vendor_invoice_number && (
                            <Typography><strong>Vendor Invoice:</strong> {formData.vendor_invoice_number}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={6} align="right">
                        <Typography><strong>Date:</strong> {formattedDate}</Typography>
                        <Typography><strong>Time:</strong> {formattedTime}</Typography>
                        <Typography><strong>Payment Method:</strong> {payment_method === 'hybrid' ? 'Cash + Online' : (_.capitalize(payment_method) || 'N/A')}</Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Items Table */}
                {items?.length > 0 && <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell width="4%">#</TableCell>
                                <TableCell width="18%">Item Name</TableCell>
                                <TableCell width="10%" align="right">Batch</TableCell>
                                <TableCell width="10%" align="right">Expiry</TableCell>
                                <TableCell width="8%" align="right">MRP (₹)</TableCell>
                                <TableCell width="8%" align="right">Price (₹)</TableCell>
                                <TableCell width="5%" align="right">Qty</TableCell>
                                <TableCell width="6%" align="right">Dis (%)</TableCell>
                                {hasGst && (
                                    <>
                                        <TableCell width="6%" align="right">Taxable</TableCell>
                                        {isIgst ? (
                                            <TableCell width="6%" align="right">IGST (%)</TableCell>
                                        ) : (
                                            <>
                                                <TableCell width="6%" align="right">CGST (%)</TableCell>
                                                <TableCell width="6%" align="right">SGST (%)</TableCell>
                                            </>
                                        )}
                                    </>
                                )}
                                {!hasGst && <TableCell width="6%" align="right">Tax (%)</TableCell>}
                                <TableCell width="8%" align="right">Amt (₹)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items?.map((item, index) => {
                                const mrp = getMRP(item);
                                const price = getPrice(item);
                                
                                const subtotalItem = price * item.quantity;
                                const discountAmountItem = subtotalItem * parseFloat(item.discount_in_perc || 0) / 100;
                                const afterDiscountItem = subtotalItem - discountAmountItem;
                                let taxAmountItem = 0;
                                if (hasGst) {
                                    taxAmountItem = parseFloat(item.cgst || 0) + parseFloat(item.sgst || 0) + parseFloat(item.igst || 0);
                                } else {
                                    taxAmountItem = afterDiscountItem * parseFloat(item.tax_in_perc || 0) / 100;
                                }
                                const finalAmount = (afterDiscountItem + taxAmountItem).toFixed(2);
                                
                                return (
                                    <TableRow key={`${item.id}-${item.batch_number}-${index}`}>
                                        <TableCell width="4%">{index + 1}</TableCell>
                                        <TableCell width="18%">{item.name}</TableCell>
                                        <TableCell width="10%" align="right">{item.batch_number}</TableCell>
                                        <TableCell width="10%" align="right">{item.expiry_date ? format(new Date(item.expiry_date), 'dd/MM/yyyy') : ''}</TableCell>
                                        <TableCell width="8%" align="right">{mrp !== null ? formatPrice(mrp) : '-'}</TableCell>
                                        <TableCell width="8%" align="right">{formatPrice(price)}</TableCell>
                                        <TableCell width="5%" align="right">{item.quantity}</TableCell>
                                        <TableCell width="6%" align="right">{parseFloat(item.discount_in_perc || 0).toFixed(0)}%</TableCell>
                                        {hasGst && (
                                            <>
                                                <TableCell align="right">{parseFloat(item.taxable_value || 0).toFixed(2)}</TableCell>
                                                {isIgst ? (
                                                    <TableCell align="right">{parseFloat(item.gst_rate || item.tax_in_perc || 0).toFixed(2)}%</TableCell>
                                                ) : (
                                                    <>
                                                        <TableCell align="right">{(parseFloat(item.gst_rate || item.tax_in_perc || 0)/2).toFixed(2)}%</TableCell>
                                                        <TableCell align="right">{(parseFloat(item.gst_rate || item.tax_in_perc || 0)/2).toFixed(2)}%</TableCell>
                                                    </>
                                                )}
                                            </>
                                        )}
                                        {!hasGst && <TableCell width="6%" align="right">{parseFloat(item.tax_in_perc || 0).toFixed(0)}%</TableCell>}
                                        <TableCell align="right" width="8%">
                                            {finalAmount}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>}

                <Divider sx={{ mb: 2 }} />

                <Grid container justifyContent="flex-end">
                    <Grid item xs={12} sm={5} md={4}>
                        <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="textSecondary">Sub Total:</Typography>
                                <Typography>₹ {subtotal.toFixed(2)}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="textSecondary">Discount:</Typography>
                                <Typography>₹ {totalDiscount.toFixed(2)}</Typography>
                            </Stack>
                            {hasGst && (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography color="textSecondary">Taxable Value:</Typography>
                                    <Typography>₹ {totalTaxableValue.toFixed(2)}</Typography>
                                </Stack>
                            )}
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Stack spacing={0}>
                                    <Typography color="textSecondary">{hasGst ? 'Total GST:' : 'Tax:'}</Typography>
                                    {hasGst && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ lineHeight: 1.2 }}
                                        >
                                            {isIgst 
                                                ? `IGST: ₹ ${totalIgst.toFixed(2)}` 
                                                : `CGST: ₹ ${totalCgst.toFixed(2)}, SGST: ₹ ${totalSgst.toFixed(2)}`}
                                        </Typography>
                                    )}
                                </Stack>
                                <Typography>₹ {totalTax.toFixed(2)}</Typography>
                            </Stack>
                            {roundOff > 0 && (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography color="textSecondary">Round Off:</Typography>
                                    <Typography>{isPositiveRoundOff ? '+' : '-'} ₹ {roundOff.toFixed(2)}</Typography>
                                </Stack>
                            )}
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="textSecondary">G.Total:</Typography>
                                <Typography>₹ {grandTotal.toFixed(2)}</Typography>
                            </Stack>
                        </Stack>
                    </Grid>
                </Grid>

                <Typography variant="body2" sx={{ mt: 2 }}>
                    {totalInWords}
                </Typography>

                <Box sx={{ mt: 4, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <Typography variant="body2" align="center">
                        नर सेवा नारायण सेवा
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

SaleInvoiceA4Content.propTypes = {
    formData: PropTypes.object.isRequired
};
